import { FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import chameleon from "@chamaeleonidae/chmln";
import * as Sentry from "@sentry/react";
import { useRouteHelpers } from "routes/helpers";
import routes from "routes/routes";
import actions from "store/actions";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { globalLoadingSelector, rewardPointsSelector } from "store/selectors";

import { getRedirectionLink, saveRedirectionLink } from "services/linkRedirector";
import { BankingType, UserRole } from "types/BETypes";
import { IUserWithCompany } from "types/user";
import { COMPANIES_LIST_FETCH_ALL_LIMIT } from "constants/company/companiesList";
import { Pages, permissions as permissionsList } from "permissions";
import { hasEmployeeRights } from "permissions/helpers/shared";
import { getName, showErrorModal } from "helpers";
import { shouldFetchInternalBankAccounts } from "helpers/bankAccount/internalBankAccount";
import {
  getShouldAdminCompleteSignUp,
  getShouldEmployeeCompleteSignUp,
  getShouldOwnerCompleteSignUp,
} from "helpers/shared/guardRedirect";
import { useEventsListener } from "hooks";
import useAuth from "hooks/useAuth";
import { useCompaniesList } from "hooks/useCompaniesList";
import { useConnectSalsa } from "hooks/useConnectSalsa";
import useSubscriptionPrices from "hooks/useSubscriptionPrices";
import ActivityTracker from "components/ActivityTracker";
import SalsaTokenRefresher from "components/SalsaTokenRefresher";

import { FullScreenLoader } from "uikit";

import {
  BankCardResDto,
  CompanyResponseDto,
  ListUserCompaniesResDto,
  queryBankCardsControllerListBankCards,
  queryUsersControllerGetRewardPoints,
  UserProfileResponseDto,
} from "utils/swagger_react_query";

import { IRoutePropsExtended } from "../../types";

const ProtectedRoute: FC<IRoutePropsExtended> = (props) => {
  const { children, permissions, disableCondition } = props;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { connectSalsa, salsaAuthToken } = useConnectSalsa();
  const { getDefaultRoute, getDefaultAdminRoute, getDefaultEmployeeRoute } = useRouteHelpers();
  const { getCurrentUserAndCompany, isAuthenticated } = useAuth();
  const globalLoading = useAppSelector(globalLoadingSelector);
  const rewardPoints = useAppSelector(rewardPointsSelector);

  const [userLoaded, setUserLoaded] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<UserProfileResponseDto | null>(null);
  const [currentCompany, setCurrentCompany] = useState<CompanyResponseDto | null>(null);
  const [bankCards, setBankCards] = useState<BankCardResDto[]>([]);
  const [userChecked, setUserChecked] = useState<boolean>(false); //NOTE:::Additional flag that determines if we need to redirect user before showing them a page content;
  const [needsDisabling, setNeedsDisabling] = useState<boolean>(false);

  const {
    companiesList,
    actions: { refetchCompanies },
  } = useCompaniesList({ perPage: COMPANIES_LIST_FETCH_ALL_LIMIT });
  useSubscriptionPrices({});

  useEventsListener<"RoleHasUpdated">(
    "RoleHasUpdated",
    async ({ roleHasUpdated, userData }) => {
      setNeedsDisabling(roleHasUpdated);
      if (userData) {
        getAdditionalData(userData, currentCompany);
      }
    },
    [],
  );

  const saveRedirect = () => {
    saveRedirectionLink(location.pathname + location.search);
  };

  const isPermitted = (
    user?: UserProfileResponseDto | null,
    company?: CompanyResponseDto | null,
  ) => {
    if (needsDisabling) return true;
    if (!user) return false;

    const result =
      !disableCondition?.(user, company, bankCards) &&
      (!permissions ||
        permissionsList[user?.activeCompanyUser?.lastActiveRole as UserRole]?.page[
          permissions as Pages
        ]);
    return result;
  };

  const checkUserPermission = (
    user?: UserProfileResponseDto | null | undefined,
    company?: CompanyResponseDto | null,
  ) => {
    const skipPermissionsCheck = location.pathname === routes.SELECT_COMPANY;
    if (
      (!user?.activeCompanyUser?.userId || !isPermitted(user, company)) &&
      !skipPermissionsCheck
    ) {
      navigate(routes.INSUFFICIENT_PERMISSIONS);
      setUserChecked(true);
      return false;
    } else {
      setUserChecked(true);
      return true;
    }
  };

  const checkRedirects = (
    user?: UserProfileResponseDto | null,
    company?: CompanyResponseDto | null,
    pathname?: string,
  ) => {
    const shouldSkipRedirectToInsufficientPermissions = location.pathname === routes.SELECT_COMPANY;

    if (!user?.activeCompanyUser?.lastActiveRole && !shouldSkipRedirectToInsufficientPermissions) {
      navigate(routes.INSUFFICIENT_PERMISSIONS);
      return;
    }

    const redirectionLink = getRedirectionLink(true);

    if (
      redirectionLink &&
      redirectionLink !== getDefaultAdminRoute() &&
      redirectionLink !== getDefaultEmployeeRoute() &&
      redirectionLink !== routes.ROOT
    ) {
      //TODO::: Won't work on dev, because of double useEffect execution in React 18, fix later
      //NOTE:::It will redirect once, but second location.pathname will trigger this check again, and won't trigger this "if" anymore
      navigate(redirectionLink);
      return;
    } else if (
      user?.activeCompanyUser?.lastActiveRole !== UserRole.EMPLOYEE &&
      ["/", getDefaultEmployeeRoute(user, company)].includes(pathname || "")
    ) {
      navigate(getDefaultRoute(user, company));
      setUserChecked(true);
      return;
    } else if (
      user?.activeCompanyUser?.lastActiveRole === UserRole.EMPLOYEE &&
      ["/", getDefaultAdminRoute(user, company)].includes(pathname || "")
    ) {
      navigate(getDefaultRoute(user, company));
      setUserChecked(true);
      return;
    }

    checkUserPermission(user, company);
  };

  const getAdditionalData = async (
    user?: UserProfileResponseDto | null,
    company?: CompanyResponseDto | null,
  ) => {
    try {
      if (hasEmployeeRights(user) && !rewardPoints) {
        if (user?.activeCompanyUser?.isOnboardingComplete) {
          const rewardPoints = await queryUsersControllerGetRewardPoints();
          dispatch(actions.userMetaData.setUserRewardPoints(rewardPoints || null));
        }
      }
      if (!salsaAuthToken) {
        await connectSalsa(user, true);
      }
      const shouldFetchBankAccounts = shouldFetchInternalBankAccounts(user, company);
      if (shouldFetchBankAccounts) {
        const requestBankingTypeByRole =
          user?.activeCompanyUser?.lastActiveRole === UserRole.EMPLOYEE
            ? BankingType.PERSONAL
            : BankingType.BUSINESS;
        const bankCardsRes = await queryBankCardsControllerListBankCards({
          bankingType: requestBankingTypeByRole,
        });
        setBankCards(bankCardsRes?.cards || []);
        dispatch(actions.userMetaData.setBankCards(bankCardsRes.cards || null));
      } else {
        setBankCards([]);
        dispatch(actions.userMetaData.setBankCards(null));
      }
    } catch (error) {
      showErrorModal(error);
    }
  };

  async function checkUser() {
    setUserChecked(false);

    let companiesListRes: ListUserCompaniesResDto | undefined;
    if (isAuthenticated()) {
      companiesListRes = await refetchCompanies(undefined, true);
    }
    const userAndCompanyData = await getCurrentUserAndCompany();
    const companyIds = companiesListRes?.companies?.map((it) => it.companyId) || [];
    const shouldOwnerCompleteSignUp = getShouldOwnerCompleteSignUp(userAndCompanyData, companyIds);
    if (shouldOwnerCompleteSignUp) {
      saveRedirect();
      return navigate(routes.SIGN_UP_COMPLETE, { replace: true });
    }

    if (!userAndCompanyData?.user?.activeCompanyId) {
      if (userAndCompanyData?.user) setCurrentUser(userAndCompanyData.user);
      if (userAndCompanyData?.company) setCurrentCompany(userAndCompanyData.company);
      if (location.pathname !== routes.SELECT_COMPANY) {
        navigate(routes.SELECT_COMPANY);
      }
      setUserLoaded(true);
      setUserChecked(true);
      return;
    }

    const shouldAdminCompleteSignUp = getShouldAdminCompleteSignUp(userAndCompanyData?.user);
    const shouldEmployeeCompleteSignUp = getShouldEmployeeCompleteSignUp(userAndCompanyData?.user);

    if (shouldAdminCompleteSignUp) {
      saveRedirect();
      return navigate(routes.ADMIN_SIGN_UP, { replace: true });
    }
    if (shouldEmployeeCompleteSignUp) {
      saveRedirect();
      return navigate(routes.EMPLOYEE_SIGN_UP, { replace: true });
    }

    const _user = userAndCompanyData?.user?.activeCompanyUser;
    const _company = userAndCompanyData?.company;
    Sentry.setUser({
      id: _user?.userId,
      email: _user?.email,
      username: getName(userAndCompanyData?.user?.personalInfo),
      companyName: _company?.name,
      companyId: _company?.companyId,
      lastActiveRole: _user?.lastActiveRole,
      roles: _user?.companyRoles,
    });

    chameleon.identify(_user?.userId || "", {
      email: _user?.email,
      name: getName(userAndCompanyData?.user?.personalInfo),
      created: _user?.createdAt,
      role: _user?.companyRoles,
      company: {
        uid: _company?.companyId,
        name: _company?.name,
        created: _company?.createdAt,
      },
    });

    if (userAndCompanyData?.user) setCurrentUser(userAndCompanyData.user);
    if (userAndCompanyData?.company) setCurrentCompany(userAndCompanyData.company);
    checkRedirects(userAndCompanyData?.user, userAndCompanyData?.company, location?.pathname);
    await getAdditionalData(userAndCompanyData?.user, userAndCompanyData?.company);
    setUserLoaded(true);
  }

  useEffect(() => {
    checkUser();
  }, []);

  /*NOTE:::
   Ducttape for https://keepfinancial.atlassian.net/browse/PAIDSW-813
  As of now, we don't check route permissions for useNavigate, meaning the page can be accessed using buttons and breadcrumbs
  The useEffect hook below is a temporary solution. We'll have to refactor our routes to using route loaders, and check all permissions there
  The reason we stopped using hook on location.pathname change is because the route page is still loaded, before hook takes effect,
  route loaders are the possible remedy */
  useEffect(() => {
    const callback = async () => {
      let userWithCompanyData: IUserWithCompany | undefined;
      if (userLoaded && userChecked) {
        userWithCompanyData = await getCurrentUserAndCompany(true); //NOTE:::Getting data again from redux. Otherwise, we might get cached values
        checkUserPermission(userWithCompanyData?.user, userWithCompanyData?.company);
      }

      if (userWithCompanyData?.user?.activeCompanyId && userLoaded) {
        getAdditionalData(userWithCompanyData.user, userWithCompanyData.company);
      }
    };
    callback();
  }, [location.pathname]);

  useEffect(() => {
    if (currentUser && currentCompany) {
      checkRedirects(currentUser, currentCompany, location?.pathname);
    }
  }, [currentUser]);

  useEffect(() => {
    if (userLoaded && userChecked) {
      if (!currentUser && !companiesList?.total) {
        navigate(routes.SIGN_IN);
        return;
      }
      dispatch(actions.userMetaData.setRouterUserRequestInProgress(false));
    }
  }, [userLoaded, userChecked]);

  return (
    <>
      {globalLoading || !userLoaded || !userChecked ? (
        <>
          <FullScreenLoader />
        </>
      ) : (
        <>
          {currentUser && (
            <>
              <ActivityTracker />
              <SalsaTokenRefresher />
              {children}
            </>
          )}
        </>
      )}
    </>
  );
};

export default ProtectedRoute;
